// CRM layer — "متابعات محوّلة إلينا" (added Phase 6-B).
// Shared inbox: follow-ups addressed to me or to any department I belong to.
// First pickup wins.

import React, { useCallback, useEffect, useState } from 'react';
import { FiInbox, FiRefreshCw } from 'react-icons/fi';
import { crmIncomingTransfers, crmPickupTransfer, fmtDateTime, fmtHours, crmNav } from './crmApi';

const CrmTransfersPage = ({ setActivePage }) => {
  const [transfers, setTransfers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmIncomingTransfers();
      setTransfers(res.data.transfers);
      setDepartments(res.data.departments || []);
    } catch (e) {
      setMessage({ kind: 'err', text: e.response?.data?.error || 'تعذر جلب المتابعات' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickup = async (t) => {
    try {
      await crmPickupTransfer(t.id);
      setMessage({ kind: 'ok', text: 'تم استلام المتابعة — أصبحت مسؤولاً عنها' });
    } catch (e) {
      // 409 is the normal outcome when a colleague was faster.
      setMessage({
        kind: 'err',
        text: e.response?.status === 409
          ? `سبقك الموظف ${e.response.data.picked_up_by_extension} إلى استلامها`
          : (e.response?.data?.error || 'فشل الاستلام')
      });
    }
    load();
  };

  const openClient = (phone) => { crmNav.setClient(phone); setActivePage('crm-client'); };

  return (
    // Batch 5: calm/enterprise identity — `crm-calm` opts this page into the
    // token-driven surface layer in crm.css. Styling only; pickup logic, the
    // 409 first-pickup-wins handling and the department ACL text are untouched.
    <div className="crm-scope crm-calm crm-calm-page">
      <div className="page-header">
        <h1 className="page-title"><FiInbox style={{ marginLeft: 8 }} />متابعات محوّلة إلينا</h1>
        <button className="btn btn-outline btn-sm" onClick={load}><FiRefreshCw size={14} /> تحديث</button>
      </div>

      <p className="crm-page-sub" style={{ marginBottom: 14 }}>
        {departments.length
          ? <>تظهر لك المتابعات الموجّهة إليك مباشرة، والموجّهة لأقسامك: <b>{departments.join('، ')}</b>.</>
          : <>لم يتم ربطك بأي قسم بعد، لذلك تظهر لك فقط المتابعات الموجّهة إلى رقمك الداخلي.
              يمكن للمدير ربطك بقسم من شاشة «إدارة الـ CRM ← الأقسام».</>}
      </p>

      {message && (
        <div className={`crm-notice ${message.kind === 'ok' ? 'ok' : 'err'}`}>{message.text}</div>
      )}

      <div className="table-container">
        <div className="table-header">
          <div className="table-title">بانتظار الاستلام ({transfers.length})</div>
        </div>

        {loading ? (
          <div className="crm-empty">جاري التحميل...</div>
        ) : transfers.length === 0 ? (
          <div className="crm-empty">لا توجد متابعات بانتظارك</div>
        ) : (
          <div style={{ padding: 16 }}>
            {transfers.map((t) => (
              <div key={t.id} className="crm-tcard">
                <div className="crm-tcard-top">
                  <b>{t.from_name || t.from_extension}</b>
                  <span className="said">حوّل إليك متابعة</span>
                  <span className={`crm-pill ${t.to_extension ? 'crm-pill-accent' : 'crm-pill-info'}`}>
                    {t.to_extension ? 'موجّه إليك مباشرة' : `لقسم ${t.to_department}`}
                  </span>
                  <span className="crm-spacer" />
                  <span className="crm-tcard-when">
                    منذ {fmtHours((Date.now() - new Date(t.created_at).getTime()) / 36e5)} · {fmtDateTime(t.created_at)}
                  </span>
                </div>

                <div className="crm-tcard-note">{t.note}</div>

                <div className="crm-tcard-foot">
                  <span className="crm-pill crm-pill-neutral" style={{ direction: 'ltr' }}>
                    {t.phone_number}
                  </span>
                  {t.source_call_id && (
                    <span className="crm-pill crm-pill-neutral">
                      من مكالمة #{t.source_call_id}
                    </span>
                  )}
                  <span className="crm-spacer" />
                  <button className="btn btn-outline btn-sm" onClick={() => openClient(t.phone_number)}>ملف العميل</button>
                  <button className="btn btn-primary btn-sm" onClick={() => pickup(t)}>استلام المتابعة</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CrmTransfersPage;
